import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Loader2, Trash2, FileImage } from "lucide-react";
import { adminGetMedia, adminUploadMedia, adminDeleteMedia, mediaUrl } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const fmtSize = (b) => (b == null ? "" : b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);

export default function AdminMedia() {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const { data: media = [], isLoading } = useQuery({ queryKey: ["admin-media"], queryFn: adminGetMedia });

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) await adminUploadMedia(f);
      await qc.invalidateQueries({ queryKey: ["admin-media"] });
      toast.success(`Uploaded ${files.length} file(s)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await adminDeleteMedia(id);
      await qc.invalidateQueries({ queryKey: ["admin-media"] });
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <AdminShell title="Media Library" actions={
      <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="admin-media-upload-input"
        className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-sm font-medium" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
        {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload
      </button>
    }>
      <input ref={inputRef} type="file" multiple accept=".svg,.png,.jpg,.jpeg,.webp,image/*" className="hidden" onChange={upload} />

      {isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => <div key={i} className="aspect-square animate-pulse rounded-[12px] border" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }} />)}
        </div>
      ) : media.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-[14px] border border-dashed py-20" style={{ borderColor: "var(--card-border)" }}>
          <FileImage size={28} style={{ color: "var(--muted)" }} />
          <p className="mt-3 text-sm" style={{ color: "var(--text)" }}>No media yet.</p>
          <p className="mt-1 text-sm" style={{ color: "var(--muted)" }}>Upload logos and images to use across the website.</p>
        </div>
      ) : (
        <div data-testid="admin-media-grid" className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {media.map((m) => (
            <div key={m.id} data-testid="admin-media-item" className="overflow-hidden rounded-[12px] border" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }}>
              <div className="flex aspect-square items-center justify-center" style={{ background: "var(--bg-elev-1)" }}>
                <img src={mediaUrl(m.thumb_url || m.url)} alt={m.filename} className="h-full w-full object-contain p-4" loading="lazy" />
              </div>
              <div className="flex items-center justify-between gap-2 p-2.5">
                <div className="min-w-0">
                  <p className="truncate text-xs" style={{ color: "var(--text)" }}>{m.filename}</p>
                  <p className="text-[10px]" style={{ color: "var(--muted)" }}>{m.ext?.toUpperCase()} • {fmtSize(m.stored_size)}</p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid="admin-media-delete-button" aria-label="Delete" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border" style={{ borderColor: "var(--card-border)", color: "var(--danger)" }}><Trash2 size={14} /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent style={{ background: "var(--surface)", borderColor: "var(--card-border)", color: "var(--text)" }}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete this file?</AlertDialogTitle>
                      <AlertDialogDescription style={{ color: "var(--muted)" }}>This permanently removes the file from storage. Files in use by a brand cannot be deleted.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel style={{ background: "transparent", borderColor: "var(--card-border)", color: "var(--text)" }}>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(m.id)} style={{ background: "var(--danger)", color: "#fff" }}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminShell>
  );
}
